// Structured data (schema.org JSON-LD) map.
//
// AI search engines and crawlers lean heavily on JSON-LD to understand what a
// company does and who it serves. Two site-wide graphs (Organization + WebSite)
// are emitted statically in index.html; page-level Service graphs are emitted
// from each page via the <Seo> component.

export const SITE_URL = "https://www.contextral.com";
export const SITE_NAME = "Contextral";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og.webp`;

type JsonLd = Record<string, unknown>;

// --- Site-wide graphs (kept in sync with the static copies in index.html) ---

export const organizationSchema: JsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/LogoWide.webp`,
  description:
    "Contextral is an AI workflow-automation services company that builds secure, private automation into the systems organizations already run — cloud or on-prem — to search internal knowledge, automate document-heavy workflows, and stay compliant.",
  email: "support@contextral.com",
  telephone: "+1-732-664-5613",
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "sales",
    telephone: "+1-732-664-5613",
    email: "support@contextral.com",
    areaServed: "US",
    availableLanguage: ["English"],
  },
  sameAs: ["https://linkedin.com/company/contextral"],
};

export const websiteSchema: JsonLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: SITE_URL,
};

// Build a Service graph for a given page. Reuses the page's own title/description
// so the structured data always matches the visible, indexed copy.
export const buildServiceSchema = (
  pathname: string,
  name: string,
  description: string,
): JsonLd => {
  const canonicalPath = pathname.replace(/\/+$/, "") || "/";

  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Secure AI workflow automation services",
    name,
    description,
    url: `${SITE_URL}${canonicalPath}`,
    provider: {
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
    },
    areaServed: {
      "@type": "Country",
      name: "United States",
    },
    audience: {
      "@type": "BusinessAudience",
      name: "Organizations modernizing secure, document-heavy operations",
    },
  };
};

// --- Person graphs -------------------------------------------------------------
// Emits a Person graph for a named team member or advisor, tied back to the
// Organization. Used on the About page so named individuals (not just the
// company) are represented as entities search/AI systems can recognize.

export const buildPersonSchema = (
  name: string,
  jobTitle: string,
  description?: string,
): JsonLd => ({
  "@context": "https://schema.org",
  "@type": "Person",
  name,
  jobTitle,
  ...(description ? { description } : {}),
  worksFor: {
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
  },
});

// --- FAQ graphs ---------------------------------------------------------------
// Emits a FAQPage graph from a page's visible Q&A. Keeping the schema built from
// the same array the page renders guarantees the structured data always matches
// what a human (and a crawler) actually sees on the page.

export interface FaqItem {
  question: string;
  answer: string;
}

export const buildFaqSchema = (faqs: FaqItem[]): JsonLd => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  })),
});

// --- Breadcrumb graphs --------------------------------------------------------
// Emits a BreadcrumbList from the same trail rendered visibly on the page, so
// the structured data mirrors what the user sees (per Google's guidance).

export interface Crumb {
  name: string;
  path: string;
}

export const buildBreadcrumbSchema = (items: Crumb[]): JsonLd => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: `${SITE_URL}${item.path === "/" ? "" : item.path}`,
  })),
});
